import { useTranslation } from "react-i18next";

type Props = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

/** Build a compact page list with ellipses, e.g. [1, "…", 4, 5, 6, "…", 20]. */
const pageWindow = (current: number, total: number): (number | "…")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const sorted = [...new Set([1, total, current, current - 1, current + 1])]
    .filter(p => p >= 1 && p <= total)
    .toSorted((a, b) => a - b);

  // Insert an ellipsis wherever consecutive pages are more than 1 apart.
  return sorted.flatMap((p, i) =>
    i > 0 && p - sorted[i - 1]! > 1 ? ["…" as const, p] : [p],
  );
};

const arrowClass =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-border-shell px-3 text-sm font-medium text-text-medium transition hover:bg-surface-elevated hover:text-text-full disabled:cursor-not-allowed disabled:opacity-40";

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: Props) => {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  return (
    <nav
      className="mt-8 flex items-center justify-center gap-1.5"
      aria-label={t("Pagination.label")}
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={arrowClass}
        aria-label={t("Pagination.previous")}
      >
        ‹
      </button>

      {pageWindow(currentPage, totalPages).map((p, i) =>
        p === "…" ? (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={`gap-${i}`}
            className="px-1 text-sm text-text-light"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-current={p === currentPage ? "page" : undefined}
            className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-3 text-sm font-semibold transition ${
              p === currentPage
                ? "bg-accent text-ink"
                : "border border-border-shell text-text-medium hover:bg-surface-elevated hover:text-text-full"
            }`}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={arrowClass}
        aria-label={t("Pagination.next")}
      >
        ›
      </button>
    </nav>
  );
};
